import {
    userEventAcceptResponseWithProposedNewTime,
    userEventAcceptResponse,
    userEventTentativeResponseWithProposedNewTime,
    userEventTentativeResponse,
    userEventDeclineResponseWithProposedNewTime,
    userEventDeclineResponse,
} from './CalendarRollUpExpanded.locstring.json';
import loc, { formatToArray } from 'owa-localize';
import getPreviewText from '../../utils/getPreviewText';
import MailMessageBody from '../MailMessageBody';
import SentReceivedSavedTime from '../SentReceivedSavedTime';
import { observer } from 'mobx-react';
import { ItemActions } from 'owa-mail-reading-pane-item-actions-view';
import { ItemPartViewState, readingPaneStore } from 'owa-mail-reading-pane-store';
import toggleCalendarRollUpItemPartExpanded from 'owa-mail-reading-pane-store/lib/actions/rollUp/toggleCalendarRollUpItemPartExpanded';
import CalendarRollUpViewState from 'owa-mail-reading-pane-store/lib/store/schema/CalendarRollUpViewState';
import { SenderImageWrapper, SenderPersonaWrapper } from 'owa-mail-sender-persona-view';
import { ClientItem, ClientMessage, InstrumentationContext, mailStore } from 'owa-mail-store';
import {
    EditEventLink,
    lazyLogMeetingMessageActionForInvite,
    MeetingMessageActionType,
} from 'owa-meeting-message';
import MeetingResponseMessage from 'owa-service/lib/contract/MeetingResponseMessageType';

import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import * as React from 'react';
import {
    ACCEPTED_ITEM_CLASS,
    DECLINED_ITEM_CLASS,
    TENTATIVE_ITEM_CLASS,
} from 'owa-mail-reading-pane-store/lib/utils/rollUp/calendarRollUpUtils';

const styles = require('./CalendarRollUp.scss');
const classNames = require('classnames/bind').bind(styles);

const calendarRollUpActionKeys: string[] = ['-', 'Reply', 'Forward'];

export interface CalendarRollUpExpandedProps {
    calendarRollUpViewState: CalendarRollUpViewState;
    instrumentationContext: InstrumentationContext;
}

@observer
export default class CalendarRollUpExpanded extends React.Component<
    CalendarRollUpExpandedProps,
    {}
> {
    private getAttendeeResponseString(itemClass: string, hasProposeNewTime: boolean): string {
        let responseString = '';
        if (itemClass.match(ACCEPTED_ITEM_CLASS)) {
            responseString = hasProposeNewTime
                ? loc(userEventAcceptResponseWithProposedNewTime)
                : loc(userEventAcceptResponse);
        } else if (itemClass.match(TENTATIVE_ITEM_CLASS)) {
            responseString = hasProposeNewTime
                ? loc(userEventTentativeResponseWithProposedNewTime)
                : loc(userEventTentativeResponse);
        } else if (itemClass.match(DECLINED_ITEM_CLASS)) {
            responseString = hasProposeNewTime
                ? loc(userEventDeclineResponseWithProposedNewTime)
                : loc(userEventDeclineResponse);
        }

        return responseString;
    }

    private onToggleItemPartExpanded(itemPartViewState: ItemPartViewState, couldExpand: boolean) {
        return () => {
            couldExpand && toggleCalendarRollUpItemPartExpanded(itemPartViewState);
        };
    }

    private logAction(message: MeetingResponseMessage) {
        return (interactionType: MeetingMessageActionType, propertyNames?: string[]) => {
            lazyLogMeetingMessageActionForInvite.importAndExecute(
                interactionType,
                message,
                'CalendarRollUp',
                propertyNames
            );
        };
    }

    private renderAttendeeStatus(
        message: MeetingResponseMessage,
        hasProposeNewTime: boolean
    ): (string | JSX.Element)[] {
        const attendeePersona = (
            <SenderPersonaWrapper
                message={message}
                treatAsDraft={false}
                isNodePending={false}
                senderPersonaClassName={styles.senderPersonaText}
            />
        );

        return formatToArray(
            this.getAttendeeResponseString(message.ItemClass, hasProposeNewTime),
            attendeePersona
        );
    }

    private renderPreviewText(message: MeetingResponseMessage): JSX.Element {
        const previewText = getPreviewText(
            message as ClientMessage,
            false /*isNodePending*/,
            true /*defaultIfEmpty*/
        );
        return <div className={styles.previewBody}>{previewText}</div>;
    }

    private renderContentAndPNT(
        message: MeetingResponseMessage,
        hasProposeNewTime: boolean,
        hasContent: boolean
    ): JSX.Element {
        return (
            <div>
                {hasProposeNewTime && (
                    <div className={styles.newTimeContainer}>
                        <span className={styles.newTimeText}>
                            {formatMeetingTimeSpan(message.ProposedStart, message.ProposedEnd)}
                        </span>
                        <EditEventLink
                            item={message}
                            editLinkClassName={styles.editLink}
                            logAction={this.logAction(message)}
                        />
                    </div>
                )}
                {hasContent && (
                    <MailMessageBody
                        className={styles.messageBody}
                        messageBody={message.UniqueBody.Value}
                        item={message as ClientItem}
                        copyAllowed={true}
                        printAllowed={true}
                        isLoading={false}
                        undoDarkMode={false}
                        actionableMessageCardInItemViewState={null}
                    />
                )}
            </div>
        );
    }

    private renderCalendarResponseCard = (
        message: MeetingResponseMessage,
        itemPartViewState: ItemPartViewState
    ): JSX.Element => {
        const hasContent = !!message.Preview;
        const hasProposeNewTime = !!(message.ProposedStart && message.ProposedEnd);
        const couldExpand = hasContent || hasProposeNewTime;
        const { isExpanded } = itemPartViewState;
        const containerClassName = classNames(styles.calendarRollUpExpandedView, {
            couldExpand: couldExpand,
            showHover: couldExpand && !isExpanded,
        });
        const showPreviewText = !couldExpand || !isExpanded;
        return (
            <div key={`calendarResponse_${message.ItemId.Id}`} className={containerClassName}>
                <div onClick={this.onToggleItemPartExpanded(itemPartViewState, couldExpand)}>
                    <SenderImageWrapper
                        message={message}
                        displaySelf={false}
                        style={styles.senderImage}
                    />
                    <div className={styles.statusActionContainer}>
                        <div className={styles.statusContainer}>
                            <span className={styles.attendeeStatusText}>
                                {this.renderAttendeeStatus(message, hasProposeNewTime)}
                            </span>
                            <SentReceivedSavedTime
                                className={styles.previewTime}
                                time={message.DateTimeReceived}
                            />
                        </div>
                        <div className={styles.actionContainer}>
                            <ItemActions
                                viewState={itemPartViewState}
                                item={message as ClientItem}
                                shouldIncludeFullInfo={false}
                                instrumentationContext={this.props.instrumentationContext}
                                hideSurfaceButton={true}
                                customizedSubsetActionKeys={calendarRollUpActionKeys}
                                actionSource={'RPCalRollUp'}
                            />
                        </div>
                    </div>
                    {showPreviewText && this.renderPreviewText(message)}
                </div>
                {!showPreviewText &&
                    this.renderContentAndPNT(message, hasProposeNewTime, hasContent)}
            </div>
        );
    };

    render() {
        if (!readingPaneStore.conversationReadingPaneViewState) {
            return null;
        }

        const { itemPartsMap } = readingPaneStore.conversationReadingPaneViewState;

        return this.props.calendarRollUpViewState.calendarReponseNodeIds.map(nodeId => {
            const itemPartViewState = itemPartsMap.get(nodeId);
            if (!itemPartViewState) {
                return null;
            }

            const item = mailStore.items.get(itemPartViewState.itemId);
            if (!item) {
                return null;
            }

            return this.renderCalendarResponseCard(
                item as MeetingResponseMessage,
                itemPartViewState
            );
        });
    }
}
